import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { CalendarDays, Plus, Trash2, Info, Pencil } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/utils/format";

const YEARS = [2025, 2026, 2027, 2028];

export default function Holidays() {
  const [year, setYear] = useState(2026);
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: "", name: "", is_optional: false });

  const load = (y) => api.get(`/holidays?year=${y}`).then(({ data }) => setItems(data));
  useEffect(() => { setItems(null); load(year); }, [year]);

  const openNew = () => { setEditing(null); setForm({ date: `${year}-01-01`, name: "", is_optional: false }); setOpen(true); };
  const openEdit = (h) => { setEditing(h); setForm({ date: h.date, name: h.name, is_optional: h.is_optional }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/holidays/${editing.id}`, form);
      else await api.post("/holidays", form);
      toast.success("Saved"); setOpen(false); load(year);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (h) => {
    try { await api.delete(`/holidays/${h.id}`); load(year); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Holiday Calendar" description="Non-optional holidays that fall on a working day reduce the month's working days." testid="holidays-page">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="holiday-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={openNew} className="gap-1.5" data-testid="add-holiday-button"><Plus className="w-4 h-4" /> Add</Button>
      </PageHeader>

      <div className="flex gap-2.5 items-start bg-secondary/60 border border-border rounded-md px-4 py-3 mb-6 text-sm text-muted-foreground">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <p>Only Republic Day, Independence Day and Gandhi Jayanti are nationally mandated. The rest are company policy — edit freely.</p>
      </div>

      {items === null ? <Loading /> : items.length === 0 ? (
        <EmptyState icon={CalendarDays} title={`No holidays for ${year}`} description="Add holidays for this year or bulk-add from the button above." testid="holidays-empty">
          <Button onClick={openNew} className="gap-1.5"><Plus className="w-4 h-4" /> Add holiday</Button>
        </EmptyState>
      ) : (
        <div className="bg-card border border-border rounded-md divide-y divide-border" data-testid="holidays-list">
          {items.map((h) => (
            <div key={h.id} className="px-5 py-3.5 flex items-center justify-between" data-testid={`holiday-row-${h.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-14 text-center">
                  <div className="font-display font-semibold tabular">{new Date(h.date).getDate()}</div>
                  <div className="text-[11px] text-muted-foreground uppercase">{new Date(h.date).toLocaleString("en-IN", { month: "short" })}</div>
                </div>
                <div>
                  <div className="font-medium text-sm">{h.name}</div>
                  <div className="text-xs text-muted-foreground">{h.is_optional ? "Optional / restricted" : "Public holiday"}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(h)} data-testid={`edit-holiday-${h.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(h)} data-testid={`delete-holiday-${h.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit holiday" : "Add holiday"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="label-caps">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="holiday-date-input" /></div>
            <div><Label className="label-caps">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="holiday-name-input" /></div>
            <div className="flex items-center justify-between border border-border rounded-md px-3.5 py-3">
              <div><div className="text-sm font-medium">Optional holiday</div><div className="text-xs text-muted-foreground">Does not reduce working days</div></div>
              <Switch checked={form.is_optional} onCheckedChange={(v) => setForm({ ...form, is_optional: v })} data-testid="holiday-optional-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.date || !form.name.trim()} data-testid="save-holiday-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
